<template>
  <div :class="classes.option">
    <LinkWrapper
      v-if="!isDraw"
      :href="event && event.eventUrl"
      target="_blank"
      @click="() => clickOnBanner()"
    >
      <TeamBanner
        :homeOrAwayTeam="homeOrAwayTeam"
        :eventId="eventId"
        :class="[`banner-${alignment}`]"
        :alignment="alignment"
        :name="competitor && competitor.name.short"
        :height="23"
        :slope="'top'"
        :disableBadge="true"
        :customSvgPath="
          alignment === 'left'
            ? 'M0 0v24h127L120 0H0z'
            : 'M127 0v24H0L7.6 0H127z'
        "
      />
    </LinkWrapper>
    <WinCompetitor
      v-else
      :class="classes.draw"
      :eventId="eventId"
      :team="optionLabel"
    />

    <div ref="targetEl">
      <OddsButton
        :option="option"
        :class="[classes.optionButton, 'optionButton', alignment]"
        @click="handleButtonClick"
      />
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent, computed, ComputedRef, PropType, provide } from "vue";
import LinkWrapper from "./LinkWrapper.vue";
import OddsButton from "./OddsButton.vue";
import TeamBanner from "./TeamBanner.vue";
import WinCompetitor from "./WinCompetitor.vue";
import { createUseStyles } from "@fresh8/vue-jss/src";
import {
  BrandContext,
  injectBrand
} from "@fresh8/ad-data-composition/src/brand";
import { injectCardNumber, useAnalytics } from "../hooks/useAnalytics";
import {
  composeEvent,
  composeBetOption
} from "@fresh8/ad-data-composition/src/betData";
import { composePopoverForm } from "@fresh8/ad-data-composition/src/popoverForm";
import { getTranslation } from "@fresh8/ad-data-composition/src/lang";
import { SportCompetitor, Option } from "@fresh8/types/src/betData";

const useStyles = createUseStyles({
  draw: {
    textAlign: "center",
    height: "19px",
    fontSize: "10px",
    paddingTop: props => (props.alignment === "center" ? "2px" : "")
  },
  option: {
    display: "flex",
    flexGrow: 1,
    flexDirection: "column",
    justifyContent: "space-between",
    paddingBottom: "11px"
  },
  optionButton: {
    maxHeight: "23px",
    height: "23px",
    marginTop: "4px",
    "&.left": {
      marginLeft: props => (props.showBadges ? "" : "6px")
    },
    "&.right": {
      marginRight: props => (props.showBadges ? "" : "6px")
    },
    "& .odds-text": {
      fontSize: "11px"
    }
  }
});

export default defineComponent({
  name: "WinOption",
  components: {
    LinkWrapper,
    OddsButton,
    TeamBanner,
    WinCompetitor
  },
  props: {
    optionId: { type: String, required: true },
    eventId: { type: String, required: true },
    isDraw: {
      type: Boolean,
      required: false,
      default: false
    },
    competitor: {
      type: Object as PropType<SportCompetitor>,
      required: false,
      default: undefined
    },
    alignment: {
      type: String as PropType<"left" | "right" | "center">,
      default: "left" as const,
      required: false
    },
    showBadges: {
      type: Boolean,
      required: false,
      default: false
    },
    bannerWidth: {
      type: String,
      required: false,
      default: "100%"
    },
    stagger: {
      type: Number,
      default: 0
    },
    homeOrAwayTeam: {
      type: String as PropType<"home" | "away">,
      default: "home",
      required: true
    }
  },
  setup(props) {
    provide("button-transition-stagger", props.stagger);
    const event = composeEvent(props.eventId);
    const { adTemplateBranding, brand } = injectBrand() || ({} as BrandContext);
    const option = composeBetOption(props.optionId) as ComputedRef<Option>;
    const cardNumber = injectCardNumber();
    const { handleClick: clickOnBanner } = useAnalytics(
      { ...props, cardNumber: cardNumber, marketId: option.value.marketID },
      "Event Name"
    );
    const { handleClick } = useAnalytics(
      {
        ...props,
        cardNumber: cardNumber,
        marketId: option.value.marketID,
        optionName: option.value.name?.full
      },
      "Odds Button"
    );

    const handleButtonClick = () => {
      if (option.value.suspended || !props.optionId) return;
      handleClick();
    };

    const { targetEl } = composePopoverForm("Market Button", [
      {
        type: "button",
        get: () => brand?.value?.adTemplate?.market?.oddsButton || "",
        set: (value: string) => {
          if (brand.value) {
            brand.value.adTemplate.market.oddsButton = value;
          }
        }
      }
    ]);

    const drawText = computed(
      () =>
        getTranslation(window.navigator.language.toLowerCase()).ButtonTexts.Draw
    );

    const styleComputed = computed(() => ({
      ...adTemplateBranding.value,
      alignment: props.alignment,
      showBadges: props.showBadges
    }));

    const classes = useStyles(styleComputed);

    return {
      event,
      classes,
      option,
      targetEl,
      clickOnBanner,
      handleButtonClick,
      optionLabel: computed(() =>
        props.isDraw ? drawText.value : option.value.label
      )
    };
  }
});
</script>

<style></style>
